import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Reveal } from "@/components/common/Reveal";
import { SectionLabel, CtaBanner } from "@/pages/Home";

export default function About() {
  const { lang, t } = useI18n();
  useSEO({ lang, title: `${t("nav.about")} | HO-SYS`, description: t("about.heroSubtitle"), path: "/about" });

  return (
    <div className="pt-32">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <Reveal>
          <SectionLabel>{t("nav.about")}</SectionLabel>
          <h1 className="mt-5 font-display font-extrabold tracking-tighter text-white text-4xl sm:text-6xl leading-[1.02] max-w-4xl">
            {t("about.heroTitle")}
          </h1>
          <p className="mt-6 text-white/60 text-lg md:text-xl max-w-2xl">{t("about.heroSubtitle")}</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <Reveal>
            <SectionLabel>{t("about.historyTitle")}</SectionLabel>
            <p className="mt-5 text-white/60 text-lg leading-relaxed">{t("about.history")}</p>
          </Reveal>
          <Reveal delay={0.1} className="relative">
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=85&w=1200" alt="HO-SYS team"
                loading="lazy" className="w-full h-[360px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-5">
          <Reveal className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-10">
            <h3 className="font-display text-2xl font-semibold text-neon">{t("about.missionTitle")}</h3>
            <p className="mt-4 text-white/60 leading-relaxed">{t("about.mission")}</p>
          </Reveal>
          <Reveal delay={0.08} className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-10">
            <h3 className="font-display text-2xl font-semibold text-lime">{t("about.visionTitle")}</h3>
            <p className="mt-4 text-white/60 leading-relaxed">{t("about.vision")}</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <Reveal className="mb-12"><SectionLabel>{t("about.valuesTitle")}</SectionLabel></Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {t("about.values").map((v, i) => (
            <Reveal key={i} delay={i * 0.06} className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-7 hover:border-neon/40 transition-colors">
              <span className="font-display text-4xl font-black text-white/15">0{i + 1}</span>
              <h4 className="mt-4 font-display text-lg font-semibold text-white">{v.title}</h4>
              <p className="mt-2 text-white/50 text-sm leading-relaxed">{v.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <Reveal>
            <SectionLabel>{t("brand.purposeTitle")}</SectionLabel>
            <p className="mt-5 text-white/70 text-xl leading-relaxed">{t("brand.purpose")}</p>
          </Reveal>
          <Reveal delay={0.08} className="rounded-2xl border border-neon/25 bg-neon/[0.04] p-8">
            <h3 className="font-display text-lg font-semibold text-neon">{t("brand.manifestoTitle")}</h3>
            <p className="mt-4 font-display text-xl md:text-2xl font-semibold text-white leading-snug">{t("brand.manifesto")}</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <Reveal className="mb-10">
          <SectionLabel>{t("brand.diffTitle")}</SectionLabel>
          <p className="mt-4 text-white/55 text-lg">{t("brand.diffSubtitle")}</p>
        </Reveal>
        <div className="grid sm:grid-cols-3 gap-4">
          {t("brand.diff").map((d, i) => (
            <Reveal key={i} delay={i * 0.06} className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-7">
              <span className="font-display text-4xl font-black text-white/15">0{i + 1}</span>
              <h4 className="mt-4 font-display text-lg font-semibold text-white">{d.title}</h4>
              <p className="mt-2 text-white/55 text-sm leading-relaxed">{d.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <Reveal className="mb-12">
          <SectionLabel>{t("about.methodologyTitle")}</SectionLabel>
        </Reveal>
        <div className="space-y-px">
          {t("about.methodology").map((m, i) => (
            <Reveal key={m.n} delay={i * 0.05}>
              <div className="group grid md:grid-cols-12 gap-6 items-start py-8 border-t border-white/10 hover:bg-white/[0.02] transition-colors">
                <div className="md:col-span-2 font-display text-5xl font-black text-white/15 group-hover:text-neon transition-colors">{m.n}</div>
                <h3 className="md:col-span-4 font-display text-2xl font-semibold text-white">{m.title}</h3>
                <p className="md:col-span-6 text-white/55 leading-relaxed">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
